/**
 * CCT (Correlated Color Temperature) and Duv from mixed SPD using CIE 1931 2° CMF.
 * Planckian locus nearest-point method; XYZ by trapezoidal integration.
 */

const H = 6.62607015e-34;
const C = 299792458;
const K = 1.380649e-23;

/**
 * Compute tristimulus X, Y, Z from wavelength-aligned SPD and CMF arrays.
 * @param {Object} rows - Template with .wl, .xbar, .ybar, .zbar (same-length arrays)
 * @param {number[]} spdMix - Mixed SPD values, same length as wl
 * @returns {{ X, Y, Z } | null}
 */
export function computeXYZFromSPD(rows, spdMix) {
  const wl = Array.isArray(rows?.wl) ? rows.wl : [];
  const xbar = Array.isArray(rows?.xbar) ? rows.xbar : (Array.isArray(rows?.x_bar) ? rows.x_bar : []);
  const ybar = Array.isArray(rows?.ybar) ? rows.ybar : (Array.isArray(rows?.y_bar) ? rows.y_bar : []);
  const zbar = Array.isArray(rows?.zbar) ? rows.zbar : (Array.isArray(rows?.z_bar) ? rows.z_bar : []);
  const n = wl.length;
  if (
    !n ||
    !Array.isArray(spdMix) ||
    spdMix.length !== n ||
    xbar.length !== n ||
    ybar.length !== n ||
    zbar.length !== n
  ) {
    return null;
  }

  let X = 0;
  let Y = 0;
  let Z = 0;

  for (let i = 0; i < n - 1; i += 1) {
    const wl1 = Number(wl[i]);
    const wl2 = Number(wl[i + 1]);
    const dl = wl2 - wl1;
    const Savg = ((Number(spdMix[i]) || 0) + (Number(spdMix[i + 1]) || 0)) / 2;
    const xbarI = xbar[i];
    const xbarI1 = xbar[i + 1];
    const ybarI = ybar[i];
    const ybarI1 = ybar[i + 1];
    const zbarI = zbar[i];
    const zbarI1 = zbar[i + 1];
    if (
      xbarI == null || xbarI1 == null || ybarI == null || ybarI1 == null || zbarI == null || zbarI1 == null ||
      !Number.isFinite(Number(xbarI)) ||
      !Number.isFinite(Number(xbarI1)) ||
      !Number.isFinite(Number(ybarI)) ||
      !Number.isFinite(Number(ybarI1)) ||
      !Number.isFinite(Number(zbarI)) ||
      !Number.isFinite(Number(zbarI1))
    ) {
      return null;
    }
    const xavg = (Number(xbarI) + Number(xbarI1)) / 2;
    const yavg = (Number(ybarI) + Number(ybarI1)) / 2;
    const zavg = (Number(zbarI) + Number(zbarI1)) / 2;
    X += Savg * xavg * dl;
    Y += Savg * yavg * dl;
    Z += Savg * zavg * dl;
  }

  return { X, Y, Z };
}

/**
 * @param {{ X, Y, Z }} xyz
 * @returns {{ x, y } | null}
 */
export function computeChromaticity({ X, Y, Z }) {
  const sum = (Number(X) || 0) + (Number(Y) || 0) + (Number(Z) || 0);
  if (sum <= 0 || !Number.isFinite(sum)) {
    return null;
  }
  return {
    x: X / sum,
    y: Y / sum,
  };
}

/**
 * CIE 1960 UCS: xy -> u, v
 * den = -2*x + 12*y + 3; u = (4*x)/den; v = (6*y)/den
 */
export function xyToUV(x, y) {
  const xx = Number(x);
  const yy = Number(y);
  const den = -2 * xx + 12 * yy + 3;
  if (!Number.isFinite(den) || Math.abs(den) < 1e-10) {
    return null;
  }
  return {
    u: (4 * xx) / den,
    v: (6 * yy) / den,
  };
}

/**
 * Blackbody spectral radiance (relative) at wavelength λ (m) and temperature T (K).
 * B(λ,T) ∝ (2hc²/λ⁵) * 1/(exp(hc/(λkT)) - 1)
 */
function planckRadianceNm(lambdaNm, T) {
  const lam = lambdaNm * 1e-9;
  const expArg = (H * C) / (lam * K * T);
  if (expArg > 700) return 0;
  const ex = Math.exp(expArg) - 1;
  if (ex <= 0) return 0;
  return (2 * H * C * C) / (lam * lam * lam * lam * lam) / ex;
}

let locusMemoKey = null;
let locusMemoTable = null;

/**
 * Build Planckian locus table: same wavelength grid and xbar/ybar/zbar as dataset.
 * T from 1000K to 20000K (step 20K). Returns array of { T, u, v }.
 * Memoized by dataset (key = wl length + first/last wl).
 */
export function buildPlanckianLocus(rows) {
  const wl = Array.isArray(rows?.wl) ? rows.wl : [];
  const xbar = Array.isArray(rows?.xbar) ? rows.xbar : (Array.isArray(rows?.x_bar) ? rows.x_bar : []);
  const ybar = Array.isArray(rows?.ybar) ? rows.ybar : (Array.isArray(rows?.y_bar) ? rows.y_bar : []);
  const zbar = Array.isArray(rows?.zbar) ? rows.zbar : (Array.isArray(rows?.z_bar) ? rows.z_bar : []);
  const n = wl.length;
  if (!n || xbar.length !== n || ybar.length !== n || zbar.length !== n) {
    return null;
  }

  const key = `${n}-${wl[0]}-${wl[n - 1]}`;
  if (locusMemoKey === key && locusMemoTable) {
    return locusMemoTable;
  }

  const locus = [];
  const T_STEP = 20;
  for (let T = 1000; T <= 20000; T += T_STEP) {
    const spd = wl.map((lambdaNm) => planckRadianceNm(Number(lambdaNm), T));
    const xyz = computeXYZFromSPD(rows, spd);
    if (!xyz) continue;
    const xy = computeChromaticity(xyz);
    if (!xy) continue;
    const uv = xyToUV(xy.x, xy.y);
    if (!uv) continue;
    locus.push({ T, u: uv.u, v: uv.v });
  }

  locusMemoKey = key;
  locusMemoTable = locus;
  return locus;
}

/**
 * Nearest point on segment (u1,v1)-(u2,v2) to point (qu,qv). Returns { t, distSq, px, py }.
 * t in [0,1], (px,py) closest point.
 */
function nearestOnSegment(u1, v1, u2, v2, qu, qv) {
  const du = u2 - u1;
  const dv = v2 - v1;
  const lenSq = du * du + dv * dv;
  let t = lenSq <= 0 ? 0 : ((qu - u1) * du + (qv - v1) * dv) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const px = u1 + t * du;
  const py = v1 + t * dv;
  const dx = qu - px;
  const dy = qv - py;
  const distSq = dx * dx + dy * dy;
  return { t, distSq, px, py, du, dv };
}

/**
 * CCT + Duv from (u,v) by nearest-point on Planckian locus.
 * Duv = signed distance: positive = green side of locus (perpendicular to tangent).
 * @param {number} u
 * @param {number} v
 * @param {{ T, u, v }[]} locus - from buildPlanckianLocus
 * @returns {{ CCT: number | null, Duv: number | null }}
 */
export function computeCCTDuvFromUV(u, v, locus) {
  if (!Array.isArray(locus) || locus.length < 2) {
    return { CCT: null, Duv: null };
  }
  const qu = Number(u);
  const qv = Number(v);
  if (!Number.isFinite(qu) || !Number.isFinite(qv)) {
    return { CCT: null, Duv: null };
  }

  let best = { distSq: Infinity, t: 0, T1: 0, T2: 0, px: 0, py: 0, du: 0, dv: 0 };

  for (let i = 0; i < locus.length - 1; i += 1) {
    const a = locus[i];
    const b = locus[i + 1];
    const { t, distSq, px, py, du, dv } = nearestOnSegment(a.u, a.v, b.u, b.v, qu, qv);
    if (distSq < best.distSq) {
      best = { distSq, t, T1: a.T, T2: b.T, px, py, du, dv };
    }
  }

  const CCT = Math.round(best.T1 + best.t * (best.T2 - best.T1));
  const len = Math.sqrt(best.du * best.du + best.dv * best.dv);
  if (len < 1e-12) {
    return { CCT, Duv: 0 };
  }
  const dx = qu - best.px;
  const dy = qv - best.py;
  const perpRightU = best.dv / len;
  const perpRightV = -best.du / len;
  const Duv = dx * perpRightU + dy * perpRightV;

  return { CCT, Duv };
}

/**
 * Full pipeline: SPD -> XYZ -> xy -> uv -> CCT + Duv (Planckian locus).
 * Locus table must be built once from same rows and passed in.
 * @param {Object} rows - Template with wl, xbar, ybar, zbar
 * @param {number[]} spdMix - Mixed SPD array
 * @param {{ T, u, v }[] | null} locusTable - from buildPlanckianLocus(rows)
 * @returns {{ CCT: number | null, Duv: number | null, x?: number, y?: number, X?: number, Y?: number, Z?: number }}
 */
export function computeCCTFromSPD(rows, spdMix, locusTable) {
  const xyz = computeXYZFromSPD(rows, spdMix);
  if (!xyz) {
    return { CCT: null, Duv: null };
  }
  const xy = computeChromaticity(xyz);
  if (!xy) {
    return { CCT: null, Duv: null };
  }
  const uv = xyToUV(xy.x, xy.y);
  if (!uv) {
    return { CCT: null, Duv: null };
  }
  if (!locusTable || locusTable.length < 2) {
    return {
      CCT: null,
      Duv: null,
      x: xy.x,
      y: xy.y,
      X: xyz.X,
      Y: xyz.Y,
      Z: xyz.Z,
    };
  }
  const { CCT, Duv } = computeCCTDuvFromUV(uv.u, uv.v, locusTable);
  return {
    CCT,
    Duv,
    x: xy.x,
    y: xy.y,
    X: xyz.X,
    Y: xyz.Y,
    Z: xyz.Z,
  };
}
